/**
 * The offline shell's build step (PLT-10, PRD §3.3 "offline after the app resources are
 * installed"): which built files the service worker precaches, the build id it reports, and the
 * rewrite of `public/sw.js` that carries both. Pure, so the build plugin in `vite.config.ts` stays
 * a thin wrapper and this can be tested without a build.
 */

/** The `<meta>` the build adds to index.html, so the page knows which build it is. */
export const BUILD_META_NAME = 'signbento-build'

/** The build id a page reports when it was not built (the dev server); the worker says the same. */
export const DEV_BUILD_ID = 'dev'

const BUILD_ID_LINE = `const BUILD_ID = '${DEV_BUILD_ID}'`
const PRECACHE_LINE = 'const PRECACHE_URLS = []'

/**
 * Every emitted file the app can ask for offline, as a root-relative URL. Source maps are left
 * out (only devtools reads them) and index.html is cached as the shell (`/`), not under its name.
 */
export function precacheUrls(fileNames: readonly string[]): readonly string[] {
  return [...new Set(fileNames)]
    .filter((name) => !name.endsWith('.map') && name !== 'index.html')
    .sort()
    .map((name) => `/${name}`)
}

/**
 * A build's id, from its emitted file names. Vite puts a content hash in every asset name, so the
 * same sources give the same id and any change to the code gives a new one, with no clock or
 * randomness in the build (00-conventions determinism rule). FNV-1a, 64-bit, as hex.
 */
export function buildIdFor(fileNames: readonly string[]): string {
  let hash = 0xcbf29ce484222325n
  const prime = 0x100000001b3n
  const mask = 0xffffffffffffffffn
  for (const char of precacheUrls(fileNames).join('\n')) {
    hash ^= BigInt(char.codePointAt(0) ?? 0)
    hash = (hash * prime) & mask
  }
  return hash.toString(16).padStart(16, '0').slice(0, 12)
}

/**
 * The built worker: `public/sw.js` with this build's id and file list. A worker whose placeholder
 * lines moved is a build error, never a worker that silently precaches nothing.
 */
export function injectServiceWorker(
  source: string,
  build: { readonly buildId: string; readonly urls: readonly string[] },
): string {
  if (!source.includes(BUILD_ID_LINE) || !source.includes(PRECACHE_LINE)) {
    throw new Error('sw.js is missing its BUILD_ID or PRECACHE_URLS placeholder line')
  }
  return source
    .replace(BUILD_ID_LINE, `const BUILD_ID = ${JSON.stringify(build.buildId)}`)
    .replace(PRECACHE_LINE, `const PRECACHE_URLS = ${JSON.stringify(build.urls)}`)
}

/** The build meta tag for index.html. */
export function buildMetaTag(buildId: string): string {
  return `<meta name="${BUILD_META_NAME}" content="${buildId}" />`
}

/** The build this page was loaded as: the meta tag's value, or the dev id when there is none. */
export function currentBuildId(doc: Pick<Document, 'querySelector'>): string {
  const meta = doc.querySelector<HTMLMetaElement>(`meta[name="${BUILD_META_NAME}"]`)
  const value = meta?.content ?? ''
  return value === '' ? DEV_BUILD_ID : value
}
